import { formatWhen } from "@/components/boo/access-copy";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { InlineError, LoadingIndicator } from "@/components/QueryState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api, type ConnectorDetail, type ConnectorVersion } from "@/lib/api";
import { hostLabel } from "@/lib/code-host";
import { connectorKeys, invalidateConnectors, movedByText, shortCommit } from "@/lib/connectors";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, RotateCcw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { ConnectorDiff } from "./ConnectorDiff";
import { BooMovesNote, HistoryGapNote } from "./parts";

// Every approved version, newest first, with who wrote and approved it and
// what changed from the one before. Any earlier version can be put back.
export function VersionsPanel({ connector, containerId }: { connector: ConnectorDetail; containerId: string }) {
  const query = useQuery({
    queryKey: connectorKeys.versions(containerId, connector.slug),
    queryFn: () => api.connectors.versions(containerId, connector.slug),
  });
  const [rollbackTo, setRollbackTo] = useState<ConnectorVersion | null>(null);
  const queryClient = useQueryClient();
  const rollback = useMutation({
    mutationFn: (commit: string) => api.connectors.rollback(containerId, connector.slug, commit),
    onSuccess: (res) => {
      setRollbackTo(null);
      toast.success(
        `${connector.name} now runs version ${shortCommit(res.runningCommit)}`,
        res.connected === false ? { description: "That version needs its connection filled in again, on the Connection tab." } : undefined,
      );
      invalidateConnectors(queryClient, containerId);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not roll back"),
  });

  if (query.isLoading) return <LoadingIndicator />;
  if (query.isError) return <InlineError error={query.error} />;
  const versions = query.data ?? [];
  if (versions.length === 0) {
    return (
      <p className="rounded-lg border p-6 text-center text-sm text-muted-foreground">
        {connector.source.kind === "boo" ? "No version has been approved yet." : "No version has run yet."}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {versions.map((v) => (
        <VersionRow
          key={v.commit}
          version={v}
          connector={connector}
          containerId={containerId}
          canRollback={connector._permissions.canConfigure && !v.current}
          onRollback={() => setRollbackTo(v)}
        />
      ))}
      <ConfirmDialog
        open={rollbackTo !== null}
        onOpenChange={(open) => !open && setRollbackTo(null)}
        variant="default"
        title={rollbackTo ? `Roll back to version ${shortCommit(rollbackTo.commit)}?` : ""}
        description={
          connector.source.kind === "boo"
            ? `${connector.name} goes back to the code ${rollbackTo?.approver ?? "someone"} approved. Action settings stay as they are; actions that version doesn’t have are no longer available.`
            : `${connector.name} goes back to this version. ${connector.main?.branch ?? "main"} on ${hostLabel(connector.source.kind)} isn’t changed, and the next merge or push there runs again. Action settings stay as they are; actions that version doesn’t have are no longer available.`
        }
        confirmLabel="Roll back"
        loading={rollback.isPending}
        onConfirm={() => rollbackTo && rollback.mutate(rollbackTo.commit)}
      />
    </div>
  );
}

function VersionRow({
  version,
  connector,
  containerId,
  canRollback,
  onRollback,
}: {
  version: ConnectorVersion;
  connector: ConnectorDetail;
  containerId: string;
  canRollback: boolean;
  onRollback: () => void;
}) {
  const [open, setOpen] = useState(false);
  const diff = useQuery({
    queryKey: connectorKeys.versionDiff(containerId, connector.slug, version.commit),
    queryFn: () => api.connectors.versionDiff(containerId, connector.slug, version.commit),
    enabled: open,
  });
  return (
    <div className="rounded-lg border">
      <div className="flex flex-wrap items-center gap-3 p-3">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-start gap-2 text-left"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
        >
          {open ? <ChevronDown className="mt-0.5 size-4 shrink-0" /> : <ChevronRight className="mt-0.5 size-4 shrink-0" />}
          <div className="min-w-0">
            <p className="text-sm font-medium leading-tight">
              <code className="mr-2 font-mono text-xs text-muted-foreground">{shortCommit(version.commit)}</code>
              {version.summary}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Written by {version.author} · {wentLive(version, connector)}, {formatWhen(version.approvedAt)}
            </p>
          </div>
        </button>
        {version.current && <Badge variant="secondary">Running</Badge>}
        {canRollback && (
          <Button size="sm" variant="outline" className="gap-1.5" onClick={onRollback}>
            <RotateCcw className="size-3.5" />
            Roll back to this
          </Button>
        )}
      </div>
      {connector.source.kind !== "boo" && (version.includesBooMoves?.length || version.historyFrom) ? (
        <div className="-mt-2 px-3 pb-3 pl-9">
          <BooMovesNote moves={version.includesBooMoves} host={connector.source.kind} repoUrl={connector.source.repoUrl} />
          <HistoryGapNote from={version.historyFrom} />
        </div>
      ) : null}
      {open && (
        <div className="border-t p-3">
          {diff.isLoading ? (
            <LoadingIndicator />
          ) : diff.isError ? (
            <InlineError error={diff.error} />
          ) : (
            <ConnectorDiff
              files={diff.data?.files ?? []}
              before={diff.data?.baseVariables ?? connector.variables}
              after={diff.data?.versionVariables ?? connector.variables}
            />
          )}
        </div>
      )}
    </div>
  );
}

// Boo-hosted: "Approved by Jane". Code host: "Merged by rita (!4)", "Pushed
// by root", or, for versions from before the backend said how,
// "Put on main by rita".
function wentLive(v: ConnectorVersion, connector: ConnectorDetail): string {
  const host = connector.source.kind;
  if (host === "boo") return `Approved by ${v.approver}`;
  if (v.movedBy) {
    const text = movedByText(v.movedBy, host);
    return text[0].toUpperCase() + text.slice(1);
  }
  return `Put on ${connector.main?.branch ?? "main"} by ${v.approver}`;
}
